import Stepper from "common/Stepper";
import Modal from "Components/Common/Modal";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import ComplainantDetails from "./ComplainantDetails";
import ComplainDetails from "./ComplainDetails";
import OtherDetails from "./OtherDetails";
import { getCGRFApplicationSchema } from "./complaintFormValidationSchmea";
import {
  useLazyGetCGRFGrievanceData,
  usePostGrievanceForm,
  usePutGrievanceForm,
} from "apis/CGRFGiftPCL";
import { omit } from "lodash";
import { generateFormData } from "utils";
import { Backdrop } from "@mui/material";
import { getOptionValue } from "common/enums";

const ComplaintModal = ({
  t,
  isOpen,
  handleClose,
  applicationId,
  isSuperAdmin,
}) => {
  const [currentStep, setCurrentStep] = useState(0);

  const [
    getApplicationData,
    {
      data: { data: applicationData } = {},
      isLoading: isApplicationDataLoading,
    },
  ] = useLazyGetCGRFGrievanceData();
  const [
    updateGrievanceForm,
    { isLoading: isSubmitting, isSuccess: isSubmitted },
  ] = usePutGrievanceForm();
  const [createGrievanceForm, { isLoading: isUpdating, isSuccess: isUpdated }] =
    usePostGrievanceForm();

  const initialValues = useMemo(
    () => ({
      complainantDetails: {
        id: applicationData?.id ?? 0,
        utilityConsumerId: applicationData?.utilityConsumerId ?? 0,
        consumerName: applicationData?.consumerName ?? "",
        address: applicationData?.address ?? "",
        pinCode: applicationData?.pinCode ?? "",
        mobileNumber: applicationData?.mobileNumber ?? "",
        email: applicationData?.email ?? "",
        faxNo: applicationData?.faxNo ?? "",
        distributionLicensee:
          applicationData?.distributionLicensee?.toUpperCase() ?? "GIFTPCL",
      },
      complainDetails: {
        grievanceCategoryName:
          applicationData?.grievanceCategories?.map(({ id, name }) => ({
            label: name,
            value: id,
          })) ?? [],
        otherCategory: "",
        employeeName: applicationData?.employeeName ?? "",
        complaintDetails: applicationData?.complaintDetails ?? "",
        reliefNature: applicationData?.reliefNature ?? "",
        documents: applicationData?.documents ?? [],
      },
      otherDetails: {
        declaration: applicationData?.declaration ?? false,
        isNomineeAdded: applicationData?.isNomineeAdded ?? false,
        nomineeName: applicationData?.nomineeName ?? "",
        nomineeAddress: applicationData?.nomineeAddress ?? "",
      },
    }),
    [applicationData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    initialErrors: {
      complainantDetails: { utilityConsumerId: "Consumer Number is required" },
      complainDetails: {
        grievanceCategoryName: "Category of grievance is required",
      },
    },
    validationSchema: getCGRFApplicationSchema[currentStep],
    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        await createGrievanceApplication(values);
      } else {
        setCurrentStep((prev) => prev + 1);
        actions.setTouched({});
      }
    },
  });

  const { errors, setFieldTouched, validateForm } = formik;

  const createGrievanceApplication = async (values, isDraft = false) => {
    const applicationDetails = omit(
      {
        ...values.complainantDetails,
        ...values.complainDetails,
        grievanceCategoryName:
          values.complainDetails.grievanceCategoryName.reduce((acc, item) => {
            if (item.value === 0) {
              acc.push(values.complainDetails.otherCategory);
            } else {
              acc.push(item.label);
            }
            return acc;
          }, []),
        ...values.otherDetails,
        isDraft,
      },
      [
        "id",
        "consumerName",
        "address",
        "pinCode",
        "mobileNumber",
        "email",
        "isNomineeAdded",
        "otherCategory",
      ]
    );
    const formData = new FormData();
    generateFormData(formData, applicationDetails);
    try {
      (await values.complainantDetails.id)
        ? updateGrievanceForm(values.complainantDetails.id, formData)
        : createGrievanceForm(formData);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDraft = async () => {
    const errors = await validateForm();
    await setFieldTouched("complainDetails.grievanceCategoryName");
    if (!errors?.complainDetails?.grievanceCategoryName) {
      createGrievanceApplication(formik.values, true);
    }
  };

  useEffect(() => {
    if (!(isSubmitting || isUpdating) && (isSubmitted || isUpdated)) {
      handleClose();
    }
  }, [isSubmitting, isUpdating]);

  const { handleSubmit } = formik;
  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t("Applicant Details"),
        isCompleted: currentStep >= 1,
        component: <ComplainantDetails formik={formik} isDisabled={false} />,
      },
      {
        id: 2,
        name: t("Complain Details"),
        isCompleted: currentStep >= 2,
        component: <ComplainDetails formik={formik} isDisabled={false} />,
      },
      {
        id: 3,
        name: t("Other Details"),
        isCompleted: currentStep === 3,
        component: <OtherDetails formik={formik} isDisabled={false} />,
      },
    ],
    [currentStep, formik, applicationData]
  );

  const getApplicationDataByApplicationId = async (applicationId) => {
    try {
      await getApplicationData(applicationId);
    } catch (error) {}
  };

  useEffect(() => {
    if (applicationId) {
      getApplicationDataByApplicationId(applicationId);
    }
  }, [applicationId]);

  return (
    <Modal
      handleClose={handleClose}
      isOpen={isOpen}
      maxWidth="lg"
      title={`${applicationId ? "Edit" : "Add"} Complaint Form`}
    >
      <div>
        <div className="stepper-container">
          <Backdrop open={isSubmitting || isUpdating} />
          <Stepper
            steps={steps}
            handleSubmit={handleSubmit}
            handleDraft={handleDraft}
            activeStepIndex={currentStep}
            setActiveStepIndex={setCurrentStep}
            isLoading={isSubmitting || isUpdating}
            formik={formik}
            isDraftDisabled={
              (!!applicationId &&
                getOptionValue("cgrfStatus", "Saved") !==
                  applicationData?.status) ||
              currentStep === 0
            }
          />
        </div>
      </div>
    </Modal>
  );
};

ComplaintModal.propTypes = {
  handleClose: PropTypes.func,
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
  t: PropTypes.func,
};

export default withTranslation()(ComplaintModal);
